import { MyTasksDto, TaskDto } from '../models';
import { isOwnTimerRunning, isRunning } from '../components/task-work-time.model';
import { isHandedOutStep, isWaitRunning } from '../components/task-step-kind.model';

/**
 * UX-20 (F-24) — Wählt die EINE Aufgabe, die jetzt dran ist.
 *
 * Die Mitarbeitersicht listete bisher drei gleichrangige Blöcke (laufende Schicht,
 * heute, später). Wer die App im Vorbeigehen aufmacht, will aber nicht
 * priorisieren, sondern wissen, was als Nächstes zu tun ist.
 *
 * Reihenfolge:
 * 0. **läuft gerade** (2.1 W1, Stoppuhr) — woran man arbeitet, ist „jetzt dran", egal in
 *    welcher Gruppe der Server sie führt. Vorher lag sie zugeklappt unter „Später".
 * 1. überfällig — das älteste zuerst (es wartet am längsten)
 * 2. heute fällig — das nächste
 * 3. laufende Schicht — das erste
 * 4. sonst `null` (die Ansicht zeigt dann ihren Leerzustand)
 *
 * Erledigte und stornierte Aufgaben kommen nie infrage, auch wenn sie überfällig
 * markiert sind.
 *
 * AP-42: Mit „Schritt-Typen" (`opts.stepKinds`) sind auch ausgegebene Extern-Schritte und
 * laufende Wartezeiten kein Kandidat — an ihnen ist jetzt nichts zu tun.
 */
export function pickFocusTask(
  my: MyTasksDto | null,
  now: Date,
  opts: { stepKinds?: boolean } = {},
): TaskDto | null {
  if (!my) return null;

  const notAway = (t: TaskDto): boolean =>
    !opts.stepKinds || (!isHandedOutStep(t, true) && !isWaitRunning(t, now, true));
  const candidate = (t: TaskDto): boolean => isFocusCandidate(t) && notAway(t);

  const candidates = [
    ...(my.CurrentShift ?? []),
    ...(my.Today ?? []),
    ...(my.Later ?? []),
  ].filter(candidate);

  const running = runningTasks(my);
  if (running.length) return running[0];

  const overdue = candidates
    .filter((t) => t.IsOverdue)
    .sort((a, b) => dueMs(a) - dueMs(b));
  if (overdue.length) return overdue[0];

  const today = (my.Today ?? [])
    .filter(candidate)
    .sort((a, b) => dueMs(a) - dueMs(b));
  if (today.length) return today[0];

  const shift = (my.CurrentShift ?? []).filter(candidate);
  if (shift.length) return shift[0];

  // `now` fließt für die Überfälligkeit bewusst nur über `IsOverdue` (serverautoritativ) ein —
  // eine eigene Rechnung wäre eine zweite Wahrheit neben dem Server. Nur die Wartezeit-Frist
  // (AP-42) vergleicht mit `now`.
  return null;
}

/**
 * 2.1 W1 — Aufgaben, an denen MEINE Stoppuhr läuft, zuletzt gestartete zuerst. „Meine" heißt:
 * `Pause` ist erlaubt ({@link isOwnTimerRunning}). Eine sichtbare `RunningSession` allein
 * reicht nicht — die direkte Leitung sieht auch die Stoppuhr ihrer Leute, und deren Arbeit
 * ist für sie nicht „jetzt dran".
 * Über alle Blöcke (Schicht, Heute, Später), ohne Doppelte; Töpfe zählen nicht — dort hat
 * noch niemand übernommen. Normalfall ist höchstens eine (der Server lehnt eine zweite
 * laufende Stoppuhr mit 409 ab), die Sortierung macht den Ausnahmefall trotzdem eindeutig.
 */
export function runningTasks(my: MyTasksDto | null): TaskDto[] {
  if (!my) return [];
  const seen = new Set<string>();
  return [...(my.CurrentShift ?? []), ...(my.Today ?? []), ...(my.Later ?? [])]
    .filter((t) => isActionable(t) && isRunning(t) && isOwnTimerRunning(t))
    .filter((t) => (seen.has(t.EventId) ? false : (seen.add(t.EventId), true)))
    .sort((a, b) => startMs(b) - startMs(a));
}

function startMs(t: TaskDto): number {
  const ms = Date.parse(t.RunningSession?.StartedAtUtc ?? '');
  return Number.isNaN(ms) ? 0 : ms;
}

/** Eine erledigte oder stornierte Aufgabe ist nie „jetzt dran". */
function isActionable(t: TaskDto): boolean {
  return t.Status !== 'Completed' && t.Status !== 'Cancelled';
}

/**
 * AP-37 — Kandidat für „Jetzt dran": offen UND nicht wartend. Ein wartender Schritt
 * (`IsDependencyBlocked`) kann nicht begonnen werden; als Fokus-Karte stünde dort eine
 * Aufgabe, deren einzige Aktion gesperrt ist. Eine laufende Stoppuhr zählt trotzdem
 * ({@link runningTasks}) — woran man arbeitet, ist dran.
 */
function isFocusCandidate(t: TaskDto): boolean {
  return isActionable(t) && t.IsDependencyBlocked !== true;
}

/** Fälligkeit als Zahl; ohne Fälligkeit ans Ende. */
function dueMs(t: TaskDto): number {
  const raw = t.DueAtUtc ?? t.DueLocalDate;
  if (!raw) return Number.MAX_SAFE_INTEGER;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? Number.MAX_SAFE_INTEGER : ms;
}

/**
 * Wie viele der eigenen Aufgaben überfällig sind — die Zahl fürs Kopf-Badge.
 * Zählt über alle Blöcke, nicht nur den sichtbaren.
 */
export function countOverdue(
  my: MyTasksDto | null,
  opts: { skipWaiting?: boolean } = {},
): number {
  if (!my) return 0;
  // AP-37: Mit „Aufträge nutzen" zählt ein wartender Schritt (`IsDependencyBlocked`) nicht —
  // er steht unter „Kommt als Nächstes", lässt sich nicht beginnen und hat kein Sprungziel.
  // Sonst meldete der Kopf „1 überfällig", und „Ansehen" lief ins Leere (Live-Befund 03.10.).
  const counts = (t: TaskDto) =>
    t.IsOverdue && isActionable(t) && !(opts.skipWaiting && t.IsDependencyBlocked === true);
  return [...(my.CurrentShift ?? []), ...(my.Today ?? []), ...(my.Later ?? [])].filter(counts).length;
}

/**
 * Der nächste anstehende Termin für den Leerzustand („Alles erledigt" allein lässt
 * offen, ob heute noch etwas kommt).
 */
export function nextDueTask(my: MyTasksDto | null): TaskDto | null {
  if (!my) return null;
  const upcoming = [...(my.Today ?? []), ...(my.Later ?? [])]
    .filter(isActionable)
    .filter((t) => dueMs(t) !== Number.MAX_SAFE_INTEGER)
    .sort((a, b) => dueMs(a) - dueMs(b));
  return upcoming[0] ?? null;
}
